import React, {useRef, useState} from 'react';

import ReactDOM from "react-dom";
import {useOpportunity} from "../../../providers/OpportunityProvider";
import CoverLetterTemplateModal from "../../proposals/components/CoverLetterTemplateModal";
import Form, {FormInputComponent} from "../../../components/form/Form";
import CoverLetterChatGPTModal from "../../proposals/components/CoverLetterChatGPTModal";
import SpinLoader from "../../../components/SpinLoader";
import Opportunity from "../../../models/Opportunity";


const MatchedTalentCoverLetterEditTab = ({opportunity, ...props}) => {
    const {
        isDisabled,
        onAccept,
        onReject,
        isUpdating,
        onUpdateJob,
        onUpdate,
        opportunity: agencyOpportunity
    } = useOpportunity();


    const [coverLettersResponse, setCoverLettersResponse] = useState(undefined);

    const editable = opportunity.canEditProposal();

    const formRef = useRef(null);

    const fieldProps = ['cover_letter', 'relevant_experience']
    const [formModel, setFormModel] = useState(TB.pick(opportunity?.job_candidate || {}, fieldProps));

    const hasAccessToEdit = opportunity.canAdmin() || opportunity.isProposingAsIndividual()

    const applyCoverLetter = async (params = {}) => {
        if (Object.keys(params).length) {
            await resetForm({...formModel, ...params});
        }
    };

    const mapping = (model) => {
        model.cover_letter = model.cover_letter || '';
        return model;
    }

    const hasFormModelChanged = () => {
        let opp = mapping(TB.pick(opportunity, fieldProps));
        let model = mapping(TB.pick(formModel, fieldProps))
        return !TB.isEqual(opp, model)
    }

    const resetForm = async (params = {}) => {
        let model = TB.pick(opportunity, fieldProps)

        model = mapping({...model, ...params});

        await formRef.current.forceReset(model);
        await formRef.current.formRef.current.formsyForm.setFormPristine(true);
        return setFormModel(model)
    }

    const submitForm = async() => {
        let invalid = formRef.current.forceValidate(formModel);
        if (invalid) {
            const node = ReactDOM.findDOMNode(invalid);
            return;
        }
        let updateParams = {
            job_candidate: formModel,
            talent_proposed_at: opportunity.talent_proposed_at || new Date()
        };

        if (props.onUpdate) {
            return props.onUpdate(updateParams)
        }
        if (!opportunity.job_candidate?.talent_proposed_at) {
            updateParams.job_candidate = {
                ...(opportunity.job_candidate || {}),
                ...(updateParams.job_candidate || {}),
                talent_proposed_at: new Date()
            };
            if (updateParams.job_candidate?.latest_proposal && !(updateParams.job_candidate?.latest_proposal instanceof Blob || updateParams.job_candidate?.latest_proposal instanceof File)) {
                delete updateParams.job_candidate?.latest_proposal;
            }
        }

        let updatedOpp = await onUpdate(updateParams, opportunity);

        if (updatedOpp) {
            return TB.sysAlert('talent.opportunity_proposal_save_btn_click')
        }
        let success = await onUpdate()

        if (success) {
            await resetForm();
        }
    }

    let letterRows = TB.max([
        TB.ceil((opportunity.job_candidate?.cover_letter || '')?.length / 60), 6
    ]);
    let expRows = TB.max([
        TB.ceil((opportunity.job_candidate?.relevant_experience || '')?.length / 60), 6
    ]);

    return (
        <SpinLoader isLoading={isUpdating}>

            <div className="opp-talent-proposal-form">
                <Form
                    ref={formRef}
                    background={"transparent"}
                    mapping={mapping}
                    onSubmit={submitForm}
                    submitButton={false}
                    isDisabled={isDisabled}
                    model={formModel}
                    fields={[]}
                    className={
                        TB.mapClass({
                            'no-edit': !editable
                        })
                    }
                    onChange={model => setFormModel({
                        ...formModel, ...model
                    })}>

                    <div className="lg:flex px-3.5 basis-full flex-row items-center my-2 gap-3">
                        <p className="proposal-tab-subheader mb-0 mt-0">
                            A good cover letter expresses your talent's excitement over the opportunity
                            and includes at least 3-5 bullets on why they are a good fit for the job.
                        </p>

                        <CoverLetterChatGPTModal
                            isDisabled={isDisabled}
                            onSubmit={applyCoverLetter}
                            requestParams={{talent_user_id : opportunity.user?.id}}
                            proposalsGenerated={opportunity?.proposal_generations_count}
                            onUpdate={() => {
                                onUpdateJob({
                                    ...agencyOpportunity,
                                    matching_talent: TB.map(agencyOpportunity.matching_talent, talentOpp => {
                                        return Opportunity.make({
                                            ...talentOpp,
                                            proposal_generations_count: (talentOpp?.proposal_generations_count || 0) + 1
                                        })
                                    })
                                })
                            }}
                            onFetch={setCoverLettersResponse}
                        />
                    </div>
                    <div className="field-section flex-row">
                        <div className="field px-3.5 basis-full">
                            <FormInputComponent
                                key={`cover-letter-${formModel.selected_cover_letter}`}
                                fieldName="cover_letter"
                                value={formModel.cover_letter || ''}
                                label="Introduction"
                                placeholder="Enter cover letter"
                                type={'editor'}
                                extra={[
                                    (coverLettersResponse === undefined || coverLettersResponse?.length > 0) ?
                                        <CoverLetterTemplateModal
                                            requestParams={{talent_user_id: opportunity.user?.id}}
                                            isDisabled={isDisabled}
                                            onSubmit={applyCoverLetter}
                                            onFetch={setCoverLettersResponse}
                                        /> : undefined

                                ]}
                                disabled={!hasAccessToEdit}
                                required={true}
                                style={styles.field}
                                maxLength={5000}
                                minLength={50}
                                validation={{
                                    rules: {
                                        maxLength: 10000,
                                    },
                                    errors: {
                                        maxLength: TB.trans('validation.max.string', {
                                            'attribute': 'cover letter',
                                            'max': 10000
                                        }),
                                        minCharLength: TB.trans('validation.min.string', {
                                            'attribute': 'cover letter',
                                            'min': 50
                                        })
                                    }
                                }}
                                enableScroll={true}
                                columns={8}
                                rows={letterRows}
                            />

                        </div>
                    </div>

                    <div className="field-section flex-row">
                        <div className="field px-3.5 basis-full">
                            <FormInputComponent
                                key={`relevant-experience-${formModel.selected_relevant_experience}`}
                                fieldName="relevant_experience"
                                value={formModel.relevant_experience || ''}
                                placeholder={hasAccessToEdit ?
                                    'Highlight your specific experience based on the client request' : (
                                        !opportunity.isProposingAsIndividual() && 'Waiting for team admin'
                                    )}
                                type={'editor'}
                                enableScroll={true}
                                required={false}
                                disabled={!hasAccessToEdit}
                                style={styles.field}
                                maxLength={5000}
                                columns={8}
                                rows={expRows}
                            />
                        </div>
                    </div>
                    {hasAccessToEdit && editable && (
                        <div className="mt-3 flex flex-row justify-end">
                            <button type="submit" className="btn btn-primary mr-3"
                                    disabled={isDisabled || !hasFormModelChanged()}>
                                {opportunity.has_shown_interest_in_job ? 'Save' : 'Submit & Show Interest'}
                            </button>
                            <button className="btn btn-outline-dark"
                                    type="button" onClick={() => {
                                        if (props.onUpdate) {
                                            return props.onUpdate()
                                        }
                                        return resetForm()
                                    }}
                                    disabled={isDisabled || !hasFormModelChanged()}>
                                Cancel
                            </button>
                        </div>
                    )}
                </Form>
            </div>
        </SpinLoader>
    );
};

const styles = {
    fieldSectionCol: {
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: 0,
    },
    rowField: {padding: '0 15px', marginBottom: 0}
};


export default MatchedTalentCoverLetterEditTab;